/*
  Admin › Users (27) — wave 2 ideas 6 and 7: the password field of "Reset password" and "Add user".
  Hidden by default with an eye to show it, "Generate" for a strong one, "Copy", and the server's rules
  (security_utils.validate_password) ticked off live while typing — no window.prompt, no guessing.
*/
import { useState } from 'react';
import { Check, Circle, Copy, Eye, EyeOff, WandSparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { generatePassword, passwordChecks } from '@/components/admin/adminModel';

export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand && document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error('Copy is blocked in this browser');
}

/**
 * readOnly: the password is already saved and only needs copying (the browser blocked the automatic copy):
 * shown in clear text, selected on focus, no Generate and no rules.
 */
export default function PasswordField({ id, value, onChange, label = 'Password', autoFocus, testIdPrefix = 'password', onCopied, readOnly = false }) {
  const [visible, setVisible] = useState(readOnly);
  const checks = passwordChecks(value);
  const rulesId = `${id}-rules`;

  const generate = () => {
    onChange(generatePassword());
    setVisible(true);
  };

  const copy = async () => {
    if (!value) return;
    try {
      await copyText(value);
      onCopied?.();
      notify.success('Password copied', { description: 'Paste it into a private message — it is not shown again after you close this.' });
    } catch (err) {
      notify.error('Could not copy the password', { description: getErrorMessage(err) });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-[12.5px] font-semibold text-ink-2">{label}</label>
        {readOnly ? null : (
          <Button type="button" variant="ghost" size="xs" onClick={generate} data-testid={`${testIdPrefix}-generate`}>
            <WandSparkles aria-hidden="true" /> Generate strong password
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            type={visible ? 'text' : 'password'}
            value={value}
            onChange={readOnly ? undefined : (event) => onChange(event.target.value)}
            readOnly={readOnly}
            onFocus={readOnly ? (event) => event.target.select() : undefined}
            autoComplete="new-password"
            spellCheck={false}
            autoFocus={autoFocus}
            aria-describedby={readOnly ? undefined : rulesId}
            className={cn(
              'h-9 w-full rounded-control border border-line bg-surface pl-3 pr-10 text-[13.5px] text-ink placeholder:text-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              visible && value && 'font-mono tracking-wide'
            )}
            placeholder="At least 12 characters"
            data-testid={`${testIdPrefix}-input`}
          />
          <button
            type="button"
            onClick={() => setVisible((current) => !current)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
            className="absolute right-1 top-1/2 grid h-7 w-8 -translate-y-1/2 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`${testIdPrefix}-toggle`}
          >
            {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
        <Button type="button" variant="outline" onClick={copy} disabled={!value} data-testid={`${testIdPrefix}-copy`}>
          <Copy aria-hidden="true" /> Copy
        </Button>
      </div>
      {readOnly ? null : (
        <ul id={rulesId} className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]" aria-label="Password rules" data-testid={`${testIdPrefix}-rules`}>
          {checks.map((check) => (
            <li key={check.key} className={cn('flex items-center gap-1.5', check.ok ? 'text-status-green-ink' : 'text-ink-3')} data-ok={check.ok}>
              {check.ok
                ? <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} aria-hidden="true" />
                : <Circle className="h-3 w-3 shrink-0 text-ink-4" aria-hidden="true" />}
              <span>{check.label}<span className="sr-only">{check.ok ? ' — done' : ' — missing'}</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
